import type { BlockSpecificV0, BlockZeroV0, DefaultBlock } from '../schemas';
import type { CssResolvers, CssVars } from '../../css-vars';

import type { Device } from '../../utils';
import { DEVICES, DEVICE_SUFFIX, atDevice } from '../../utils';
import {
    backgroundFilterVar,
    backgroundLayerVar,
    backgroundTintLayer,
    backgroundVars,
} from '../../features/background/background.css-vars';
import { borderVars, borderWidthVars } from '../../features/border/border.css-vars';
import { colorValueVar, flexAlignVar, numberWithUnitVar, responsiveSpacingVars, textAlignVar } from '../../mixins';

/**
 * CSS custom properties for a block — the replacement for the former JSS
 * `getStyles`. The static rules live in `./block.base.less`; only the per-block
 * values travel through here, so the editor and the published page compute the
 * same bag.
 *
 * **Colour and image resolution are injected**, for the same reason as the
 * section and the column: the package is framework-free, and the image resolver
 * also carries the app's own CDN base.
 */
export type BlockProps = DefaultBlock['props'];
export type BlockBackgroundProps = BlockProps['background'];

/** The content column a block gets when it has no width of its own. */
const CONTENT_WIDTH = '1200px';

/**
 * How wide the block lays its rows out — `100vw` when it is full-bleed, and the
 * content column when it has no width of its own. The default lives here rather
 * than in every stored block that never changed it.
 */
export function blockWidthVar(width: BlockProps['width'] | undefined): string {
    return numberWithUnitVar(width) ?? CONTENT_WIDTH;
}

/**
 * How tall the block is, or `undefined` when its contents decide. An unset
 * custom property makes the declaration invalid at computed-value time, so the
 * height falls back to `auto` without the LESS needing a second rule.
 */
export function blockHeightVar(
    height: BlockProps['height'] | undefined,
    device: Device = 'desktop'
): string | undefined {
    return numberWithUnitVar(atDevice(height, device));
}

/** Every variable a block needs, for all three breakpoints. */
export function getBlockCssVars(block: { props: BlockProps }, resolvers: CssResolvers): CssVars {
    const { props } = block;

    // A block whose migration failed has no `props` at all — it is handed back in
    // its persisted shape. It renders unstyled rather than throwing and taking the
    // whole page render down with it; see `blockSubType`.
    if (!props) {
        return {};
    }

    const vars: CssVars = { '--block-max-width': blockWidthVar(props.width) };

    DEVICES.forEach((device) => {
        Object.assign(
            vars,
            responsiveSpacingVars(props.padding, 'block-padding', device),
            borderVars(props.border, 'block', device),
            borderWidthVars(props.border, 'block', device),
            backgroundVars(props.background, 'block', resolvers, device)
        );

        const height = blockHeightVar(props.height, device);
        if (height) {
            vars[`--block-height${DEVICE_SUFFIX[device]}`] = height;
        }
    });

    return vars;
}

/* -------------------------------------------------------------------------- */
/*                          The specific (header) block                        */
/* -------------------------------------------------------------------------- */

/**
 * The header/footer block paints its background on the wrapper itself rather
 * than on a separate layer, and it carries a second, collapsed presentation —
 * the burger bar it folds into on a narrow screen. Those extra values live here
 * instead of in their own file so they can reuse the readers above.
 */
export type SpecificBlockProps = BlockSpecificV0['props'];

/** `ESize` — the burger/cart glyph and the round box around it. */
const CART_ICON_SIZE: Record<string, string> = { S: '19px', M: '24px', L: '29px' };
const CART_ICON_BOX_SIZE: Record<string, string> = { S: '40px', M: '48px', L: '56px' };

/**
 * The burger bar's own background — colour only. The legacy styles also tried to
 * put the mobile background *image* on the wrapper, but built the shorthand as
 * `url(...), undefined`, which every browser drops as invalid; that dead branch
 * is not carried over.
 */
export function specificBlockBarBgVar(
    background: BlockBackgroundProps | undefined,
    { colorCss }: Pick<CssResolvers, 'colorCss'>
): string {
    return colorValueVar(atDevice(background?.color, 'mobile'), colorCss);
}

export function getSpecificBlockCssVars(block: { props: SpecificBlockProps }, resolvers: CssResolvers): CssVars {
    const { props } = block;

    // A block whose migration failed has no `props` at all — it is handed back in
    // its persisted shape. It renders unstyled rather than throwing and taking the
    // whole page render down with it; see `blockSubType`.
    if (!props) {
        return {};
    }
    const { icon } = props;

    return {
        ...responsiveSpacingVars(props.padding, 'block-padding', 'desktop'),
        ...responsiveSpacingVars(props.padding, 'block-padding', 'mobile'),
        ...backgroundVars(props.background, 'block', resolvers, 'desktop'),
        '--block-bar-bg': specificBlockBarBgVar(props.background as never, resolvers),
        '--block-icon-color': resolvers.colorCss(icon.color),
        '--block-icon-size': CART_ICON_SIZE[icon.size] ?? CART_ICON_SIZE['M'],
        '--block-icon-box-size': CART_ICON_BOX_SIZE[icon.size] ?? CART_ICON_BOX_SIZE['M'],
        '--block-icon-gap-right': icon.horizontalPosition === 'R' ? '0' : '10px',
        '--block-icon-gap-left': icon.horizontalPosition === 'R' ? '10px' : '0',
        '--block-content-align': flexAlignVar(props.horizontalPosition, 'flex-start'),
        '--block-content-text-align': textAlignVar(props.horizontalPosition, 'left'),
        '--block-text-color': resolvers.colorCss(props.textColor),
    };
}

/* -------------------------------------------------------------------------- */
/*                            The zero (canvas) block                          */
/* -------------------------------------------------------------------------- */

export type ZeroBlockProps = BlockZeroV0['props'];

const isGradient = (color: string): boolean => color.includes('gradient(');

/**
 * The canvas layers its background as **longhands**, not as one `background`
 * shorthand like the other two blocks do. That is not a stylistic choice: a
 * narrow breakpoint here overrides only the colour and keeps the desktop image,
 * which a shorthand would wipe. Splitting colour from image reproduces it, and
 * the `var(--x-mobile, var(--x))` chain in the LESS carries the inheritance.
 */
export function zeroBlockBgColorVar(
    background: BlockBackgroundProps | undefined,
    { colorCss }: Pick<CssResolvers, 'colorCss'>,
    device: Device = 'desktop'
): string {
    const color = colorValueVar(atDevice(background?.color, device), colorCss);

    return isGradient(color) ? 'transparent' : color;
}

/**
 * The picked image under the overlay tint, over the colour when that colour is a
 * gradient — `background-color` drops a gradient, so it goes in as the last image
 * layer instead. `none` when there is neither, so a breakpoint never falls back to
 * the desktop layers in the LESS: `atDevice` has already inherited what it should.
 */
export function zeroBlockBgImageVar(
    background: BlockBackgroundProps | undefined,
    { colorCss, imageCss }: Pick<CssResolvers, 'colorCss' | 'imageCss'>,
    device: Device = 'desktop'
): string {
    const image = atDevice(background?.image, device);
    const color = colorValueVar(atDevice(background?.color, device), colorCss);
    // The tint is read from desktop for every breakpoint, as the legacy styles did.
    const layers = [
        image ? `${backgroundTintLayer(background, 'desktop')}${imageCss(image)}` : '',
        isGradient(color) ? color : '',
    ].filter(Boolean);

    return layers.length ? layers.join(', ') : 'none';
}

const zeroBackgroundVars = (props: ZeroBlockProps, device: Device, resolvers: CssResolvers): CssVars => {
    const suffix = DEVICE_SUFFIX[device];
    const background = props.background as never;
    const vars: CssVars = {
        [`--block-bg-color${suffix}`]: zeroBlockBgColorVar(background, resolvers, device),
        [`--block-bg-image${suffix}`]: zeroBlockBgImageVar(background, resolvers, device),
    };

    if (!atDevice(props.background?.image, device)) {
        return vars;
    }

    vars[`--block-bg-position${suffix}`] = backgroundLayerVar(background, 'position', device) as string;
    vars[`--block-bg-repeat${suffix}`] = backgroundLayerVar(background, 'repeat', device) as string;
    vars[`--block-bg-size${suffix}`] = backgroundLayerVar(background, 'size', device) as string;

    const filter = backgroundFilterVar(background, device);
    if (filter) {
        vars[`--block-bg-filter${suffix}`] = filter;
    }

    return vars;
};

export function getZeroBlockCssVars(block: { props: ZeroBlockProps }, resolvers: CssResolvers): CssVars {
    const { props } = block;

    // A block whose migration failed has no `props` at all — it is handed back in
    // its persisted shape. It renders unstyled rather than throwing and taking the
    // whole page render down with it; see `blockSubType`.
    if (!props) {
        return {};
    }
    const vars: CssVars = { '--block-max-width': blockWidthVar(props.width) };

    DEVICES.forEach((device) => {
        Object.assign(vars, borderVars(props.border, 'block', device), zeroBackgroundVars(props, device, resolvers));

        const height = blockHeightVar(props.height, device);
        if (height) {
            vars[`--block-height${DEVICE_SUFFIX[device]}`] = height;
        }
    });

    return vars;
}

/* -------------------------------------------------------------------------- */
/*                              Shape dividers                                 */
/* -------------------------------------------------------------------------- */

export type BlockShapeDividers = BlockProps['shapeDividers'];
export type BlockShapeDividerValue = BlockShapeDividers['top']['desktop'];
export type BlockShapeDividerEdge = 'top' | 'bottom';

/** The theme colour vocabulary the divider's `fill` is picked from. */
const DIVIDER_COLORS: Record<string, string> = {
    light: 'var(--light-color)',
    dark: 'var(--dark-color)',
    primary: 'var(--primary-color)',
    secondary: 'var(--secondary-color)',
    accent: 'var(--accent-color)',
};

export function blockShapeDividerColor(value: string | undefined): string {
    return DIVIDER_COLORS[value as string] ?? (value as string);
}

/** The divider settings for one edge at one breakpoint, falling back to desktop. */
export function blockShapeDividerAt(
    shapeDividers: BlockShapeDividers | undefined,
    edge: BlockShapeDividerEdge,
    device: Device = 'desktop'
): BlockShapeDividerValue | undefined {
    return atDevice(shapeDividers?.[edge], device);
}

/**
 * The SVG `<pattern>` width. `repeat` is how many times the shape tiles across
 * the block, so the pattern cell is that fraction of the width; zero means one
 * cell spanning the whole edge.
 */
export function blockShapeDividerPatternWidth(repeat: number | undefined): string {
    return repeat ? `${100 / repeat}%` : '100%';
}
